import React, { useCallback } from 'react';

import { di } from 'react-magnetic-di';

import Button from '@atlaskit/button/default/button';
import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import Radio from '@atlaskit/radio/radio';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import { asNumberValue } from '../../../../../../../../common/types/form/form-fields/field-values/utils';
import { asFormRankingField } from '../../../../../../../../common/types/form/form-fields/utils';
import type { FormFieldComponentRendererProps } from '../../../../../../../../common/types/renderers';
import { ScrollContainer } from '../../../../../../../../common/ui/scroll-container';
import { getScaleLabelsFromOptions } from '../../../../../../../../common/utils/scale/utils';
import {
	useCurrentValue,
	useFieldInvalid,
	useFormField,
	useFormRendererActions,
} from '../../../../../../../../controllers/form-renderer-store';

const styles = cssMap({
	buttonWrapper: {
		flex: 1,
	},
	labelsRow: {
		marginTop: token('space.150'),
	},
	edgeLabelStack: {
		flex: 1,
		position: 'relative',
	},
	edgeLabelText: {
		width: 'max-content',
		maxWidth: '50%',
		position: 'absolute',
		bottom: token('space.negative.300'),
		whiteSpace: 'normal',
	},
	startLabelText: {
		left: 0,
		textAlign: 'start',
	},
	endLabelText: {
		right: 0,
		textAlign: 'end',
	},
	gatedLabelSpacer: {
		height: '1.5rem',
	},
	container: {
		width: 'fit-content',
		minWidth: '100%',
	},
});

export const EditScaleInput = ({
	fieldId,
	fieldErrorId,
}: FormFieldComponentRendererProps): React.JSX.Element => {
	di(useCurrentValue, useFieldInvalid, useFormField, useFormRendererActions);

	const isRatingScaleA11yFixesEnabled = fg('proforma_rating_scale_a11y_fixes');
	const isInvalid = useFieldInvalid({ fieldId });
	const currentValue = asNumberValue(useCurrentValue({ fieldId }));
	const formField = useFormField({ fieldId });
	const formRankingField = asFormRankingField(formField);
	const { updateModifiedValue } = useFormRendererActions();

	const selectedOptionId = currentValue?.number !== undefined ? String(currentValue.number) : null;

	const onSelect = useCallback(
		(optionId: string): void => {
			if (optionId === selectedOptionId) {
				updateModifiedValue(fieldId, null);
			} else {
				const selected = formRankingField?.options.find((o) => o.id === optionId);
				if (selected) {
					updateModifiedValue(fieldId, { number: Number(optionId) });
				}
			}
		},
		[fieldId, formRankingField, selectedOptionId, updateModifiedValue],
	);

	if (formRankingField === undefined) {
		return <></>;
	}

	const { leftLabel, middleLabel, rightLabel } = getScaleLabelsFromOptions(
		formRankingField.options,
	);
	const hasLabels = Boolean(leftLabel && rightLabel);

	if (!isRatingScaleA11yFixesEnabled) {
		return (
			<ScrollContainer>
				<Box
					testId="field-input-scale-edit"
					xcss={styles.container}
					aria-invalid={isInvalid}
					aria-describedby={isInvalid ? fieldErrorId : undefined}
				>
					<Inline space="space.200">
						{formRankingField.options.map((option) => {
							const isSelected = option.id === selectedOptionId;
							const inputId = `${fieldId}-${option.id}`;
							return (
								<React.Fragment key={option.id}>
									<VisuallyHidden>
										<Radio
											id={inputId}
											value={option.id}
											label={option.id}
											name={fieldId}
											isChecked={isSelected}
											onChange={() => onSelect(option.id)}
										/>
									</VisuallyHidden>
									<Box xcss={styles.buttonWrapper}>
										<Button
											appearance="default"
											isSelected={isSelected}
											shouldFitContainer
											onClick={() => onSelect(option.id)}
											aria-hidden
											tabIndex={-1}
											testId={`field-input-scale-edit-option-${option.id}-${fieldId}`}
										>
											{option.id}
										</Button>
									</Box>
								</React.Fragment>
							);
						})}
					</Inline>
					{hasLabels && (
						<Inline
							spread="space-between"
							alignBlock="center"
							xcss={styles.labelsRow}
							testId="field-input-scale-edit-labels"
						>
							<Text
								size="small"
								color="color.text.subtle"
								testId="field-input-scale-edit-label-left"
							>
								{leftLabel}
							</Text>
							{middleLabel && (
								<Text
									size="small"
									color="color.text.subtle"
									testId="field-input-scale-edit-label-middle"
								>
									{middleLabel}
								</Text>
							)}
							<Text
								size="small"
								color="color.text.subtle"
								testId="field-input-scale-edit-label-right"
							>
								{rightLabel}
							</Text>
						</Inline>
					)}
				</Box>
			</ScrollContainer>
		);
	}

	return (
		<ScrollContainer>
			<Box
				testId="field-input-scale-edit"
				xcss={styles.container}
				aria-invalid={isInvalid}
				aria-describedby={isInvalid ? fieldErrorId : undefined}
			>
				<Box xcss={styles.edgeLabelStack}>
					<Inline
						space="space.200"
						alignBlock="start"
						role="radiogroup"
						aria-label={formRankingField.label}
					>
						{formRankingField.options.map((option, index) => {
							const isSelected = option.id === selectedOptionId;
							const inputId = `${fieldId}-${option.id}`;
							return (
								<Box key={option.id} xcss={styles.buttonWrapper}>
									<VisuallyHidden>
										<Radio
											id={inputId}
											value={option.id}
											label={option.id}
											name={fieldId}
											isChecked={isSelected}
											onChange={() => onSelect(option.id)}
										/>
									</VisuallyHidden>
									<Button
										appearance="default"
										isSelected={isSelected}
										shouldFitContainer
										onClick={() => onSelect(option.id)}
										aria-hidden
										tabIndex={-1}
										testId={`field-input-scale-edit-option-${option.id}-${fieldId}`}
									>
										{option.id}
									</Button>
									{index === 0 && hasLabels && (
										<Box
											xcss={cx(styles.edgeLabelText, styles.startLabelText)}
											testId="field-input-scale-edit-label-left"
										>
											<Text size="small" color="color.text.subtle">
												{leftLabel}
											</Text>
										</Box>
									)}
									{index === formRankingField.options.length - 1 && hasLabels && (
										<Box
											xcss={cx(styles.edgeLabelText, styles.endLabelText)}
											testId="field-input-scale-edit-label-right"
										>
											<Text size="small" color="color.text.subtle">
												{rightLabel}
											</Text>
										</Box>
									)}
								</Box>
							);
						})}
					</Inline>
				</Box>
				{hasLabels && <Box xcss={styles.gatedLabelSpacer} />}
			</Box>
		</ScrollContainer>
	);
};
